import React, { useState } from "react";
import { Button, Dialog, Grid, Divider, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import toast from "react-hot-toast";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import { useTheme } from "@material-ui/core";
import { useDispatch } from "react-redux";

import { ADD_COLLEAGUE, GET_USERS } from "../../api/user";
import { UseCompany, UseCompanyWorkspaces } from "../../hooks/company";
import { getCompanyUsers } from "../../redux/company/actions";
import messages from "../../utils/messages";
import { StyledPaper } from "../ManualEntry/manualEntryStyles";
import SoftTypography from "../../components/SoftTypography";
import SoftBox from "../../components/SoftBox";
import SoftInput from "../../components/SoftInput";
import SoftSelect from "../../components/SoftSelect";
import SoftButton from "../../components/SoftButton";
import UpgradePopUp from "../../components/ChallengeArenaPopUp/UpgradePopUp";
import ERRORS from "../../constants/errorStatus";
import AddPlayerBillingUpdate from "./AddPlayerBillingUpdate";
import AddPlayerSchema from "../../form-validations/AddPlayerSchema";
import { PHONE_EXAMPLE } from "../../utils/phone";

const AddUser = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const company = UseCompany();
  const theme = useTheme();
  const workspaces = UseCompanyWorkspaces();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [addPlayerBillingDialog, setAddPlayerBillingDialog] = useState(false);

  const openAddPlayerBillingDialog = () => setAddPlayerBillingDialog(true);
  const closeAddPlayerBillingDialog = () => setAddPlayerBillingDialog(false);

  const departmentNames = workspaces?.map((dept) => ({
    value: dept._id,
    label: dept.name,
  }));

  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const validationOpt = {
    resolver: yupResolver(AddPlayerSchema),
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      department: "",
      phoneNumber: "",
    },
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm(validationOpt);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const triggerSave = async (formData) => {
    setLoading(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName,
        email: formData.email.trim(),
        groupId: formData.department,
        companyId: company._id,
        phoneNumber: formData.phoneNumber?.trim() || "",
      };

      await ADD_COLLEAGUE(payload);
      reset();
      setLoading(false);
      handleClose();

      setTimeout(() => {
        openAddPlayerBillingDialog();
      }, 2000);
    } catch (error) {
      setLoading(false);

      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const errorMessage =
        (typeof responseData === "string" && responseData) ||
        responseData?.message ||
        responseData?.error?.message ||
        error?.message ||
        "Error while adding a player";

      if (status === ERRORS.UPGRADE_REQUIRED) {
        setShowUpgrade(true);
      }
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <SoftButton
        color="primary"
        sx={{
          color: "primary.contrastText",
          height: "40px",
          padding: "0 20px",
          borderRadius: "10px",
          textTransform: "none",
          fontFamily: "DM Sans",
          fontSize: "13px",
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
        onClick={handleOpen}
      >
        Add Player
      </SoftButton>
      {open && (
        <Dialog
          aria-labelledby="unstyled-modal-title"
          aria-describedby="unstyled-modal-description"
          open={open}
          onClose={handleClose}
          PaperComponent={StyledPaper}
          sx={{
            margin: "auto",
            width: { xs: "100vw", sm: "60vw" },
            ".MuiPaper-root": {
              borderRadius: "24px",
              width: "100%",
              background: "#FFFFFF",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              padding: { mini: "1rem", md: "1.5rem" },
              overflow: "visible",
              border: "1px solid rgba(15,23,42,0.10)",
            },
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <SoftTypography variant="h4" sx={{ color: "#0F172A" }}>
              Add Player to Workspace
            </SoftTypography>
            <SoftButton
              variant="outlined"
              color="dark"
              onClick={handleClose}
              sx={{
                "& svg": { fontSize: "1.25rem !important" },
                borderRadius: "50%",
                minWidth: "max-content",
                minHeight: "max-content",
                padding: "0.25rem",
                color: "#475467",
              }}
            >
              <CloseRoundedIcon />
            </SoftButton>
          </SoftBox>
          <Divider
            sx={{
              backgroundColor: "rgba(15,23,42,0.10)",
            }}
          />
          <SoftBox
            sx={{
              backgroundColor: "rgba(15,23,42,0.03) !important",
              borderRadius: "8px",
              border: "1px solid rgba(15,23,42,0.10)",
              padding: "10px 15px",
              display: "flex",
              marginBottom: "20px",
              gap: "0.5rem",
            }}
          >
            <ReportGmailerrorredIcon sx={{ color: "#475467" }} fontSize="medium" />
            <SoftBox>
              {/* <SoftTypography
                component="p"
                color="white"
                sx={{ fontSize: "14px", fontWeight: 500, marginBottom: "15px" }}
              >
                You're rocking our free plan, and adding players is currently on
                us! 🎉
              </SoftTypography> */}
              <SoftTypography
                component="p"
                sx={{ fontSize: "14px", fontWeight: 500, color: "#475467" }}
              >
                This is a friendly reminder that any extra players will be charged $5 each
                 in the upcoming billing cycle. Embrace the game, and enjoy the ride!
                {/* This is a friendly reminder that any extra players beyond the admin 
                seat will be charged $5 each in the upcoming billing cycle. Embrace 
                the game, and enjoy the ride! */}
              </SoftTypography>
              {/* <SoftTypography
                component="p"
                color="white"
                sx={{ fontSize: "14px", fontWeight: 500 }}
              >
                Just a friendly reminder that after your 14-day trial, any extra
                players beyond the admin seat will be charged at $5 each in the
                upcoming billing cycle. Embrace the game, and enjoy the ride!
              </SoftTypography> */}
            </SoftBox>
          </SoftBox>
          <form onSubmit={handleSubmit(triggerSave)}>
            <Grid container rowSpacing={3} columnSpacing={2} marginBottom={5}>
              <Grid item lg={6}>
                <SoftInput
                  type="text"
                  placeholder="First Name"
                  {...register("firstName")}
                  error={!!errors?.firstName}
                  helperText={errors?.firstName?.message}
                  sx={{
                    border: "1px solid #E4E7EC",
                    backgroundColor: "#F6F7F9 !important",
                    color: "#1d2939 !important",
                    "& .MuiInputBase-input::placeholder": {
                      color: "#98A2B3 !important",
                      opacity: "1",
                    },
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                    },
                  }}
                  size="large"
                />
              </Grid>
              <Grid item lg={6}>
                <SoftInput
                  type="text"
                  placeholder="Last Name"
                  {...register("lastName")}
                  error={!!errors?.lastName}
                  helperText={errors?.lastName?.message}
                  sx={{
                    border: "1px solid #E4E7EC",
                    backgroundColor: "#F6F7F9 !important",
                    color: "#1d2939 !important",
                    "& .MuiInputBase-input::placeholder": {
                      color: "#98A2B3 !important",
                      opacity: "1",
                    },
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                    },
                  }}
                  size="large"
                />
              </Grid>
              <Grid item lg={12}>
                <SoftInput
                  id="outlined-basic"
                  placeholder="Email"
                  type="email"
                  label="Email"
                  sx={{
                    border: "1px solid #E4E7EC",
                    backgroundColor: "#F6F7F9 !important",
                    color: "#1d2939 !important",
                    "& .MuiInputBase-input::placeholder": {
                      color: "#98A2B3 !important",
                      opacity: "1",
                    },
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                    },
                  }}
                  {...register("email")}
                  error={!!errors?.email}
                  helperText={errors?.email?.message}
                  size="large"
                />
              </Grid>
              <Grid item lg={12}>
                <SoftBox
                  sx={{
                    border: `1px solid ${primaryColor}33`,
                    background: `${primaryColor}0A`,
                    borderRadius: "10px",
                    padding: "12px 12px 13px",
                  }}
                >
                  <SoftBox sx={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "6px" }}>
                    <SoftTypography sx={{ fontSize: "12px", fontWeight: 600, color: "#0F172A" }}>
                      Mobile number <SoftBox component="span" sx={{ color: primaryColor }}>*</SoftBox>
                    </SoftTypography>
                    <SoftBox
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        color: primaryColor,
                        background: `${primaryColor}1F`,
                        padding: "2px 7px",
                        borderRadius: "999px",
                      }}
                    >
                      for SMS alerts
                    </SoftBox>
                  </SoftBox>
                  <SoftInput
                    id="addPlayerPhone"
                    type="tel"
                    placeholder={PHONE_EXAMPLE}
                    sx={{
                      border: "1px solid #E4E7EC",
                      backgroundColor: "#FFFFFF !important",
                      color: "#1d2939 !important",
                      "& .MuiInputBase-input::placeholder": {
                        color: "#98A2B3 !important",
                        opacity: "1",
                      },
                      "&.Mui-focused": {
                        borderColor: primaryColor,
                        boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                      },
                    }}
                    {...register("phoneNumber")}
                    error={!!errors?.phoneNumber}
                    helperText={errors?.phoneNumber?.message}
                    size="large"
                  />
                  <SoftTypography sx={{ fontSize: "11.5px", color: "#475467", marginTop: "5px", lineHeight: 1.5 }}>
                    We text performance alerts to this number. Without it, this player won&apos;t
                    receive SMS alerts.
                  </SoftTypography>
                </SoftBox>
              </Grid>
              <Grid item lg={12}>
                {/* Controller keeps react-select in sync with react-hook-form.
                    The previous register()+setValue() wiring spread RHF's
                    onBlur/ref onto react-select, which could clobber the
                    selected value and submit an empty department (players
                    created with no workspace). */}
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <SoftSelect
                      error={!!errors?.department}
                      helperText={errors?.department?.message}
                      placeholder="Choose a department"
                      options={departmentNames}
                      size="large"
                      value={
                        departmentNames?.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      onChange={(option) =>
                        field.onChange(option ? option.value : "")
                      }
                      onBlur={field.onBlur}
                      styles={{
                        color: "#1d2939 !important",
                        height: "fit-content !important",
                        placeholder: {
                          color: "#98A2B3 !important",
                          opacity: "1",
                        },
                        input: {
                          color: "#1d2939 !important",
                          opacity: "1",
                        },
                        singleValue: {
                          color: "#1d2939 !important",
                          opacity: "1",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Divider
              sx={{
                backgroundColor: "rgba(15,23,42,0.10)",
              }}
            />
            <SoftBox display="flex" justifyContent="flex-end">
              <SoftButton
                type="submit"
                size="small"
                disabled={loading}
                sx={{
                  background: primaryColor,
                  color: "#FFFFFF !important",
                  fontSize: "1.1rem",
                  borderRadius: "4px",
                  textTransform: "none",
                  border: 0,
                  boxShadow: "none",
                  "&:hover": { background: primaryColor, filter: "brightness(0.96)", boxShadow: "none" },
                }}
              >
                Add Player
                {loading && (
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    className="ml-2"
                  />
                )}
              </SoftButton>
            </SoftBox>
          </form>
        </Dialog>
      )}
      {addPlayerBillingDialog && (
        <AddPlayerBillingUpdate
          addPlayerBillingDialog={addPlayerBillingDialog}
          closeAddPlayerBillingDialog={closeAddPlayerBillingDialog}
        />
      )}
      {showUpgrade && (
        <UpgradePopUp
          showUpgrade={showUpgrade}
          closeUpgrade={() => setShowUpgrade(false)}
        />
      )}
    </>
  );
};

export default AddUser;
